import type { ReactNode } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { BookingCompanionsPanel } from "@/components/bookings/BookingCompanionsPanel";
import { GuestInquirySummary } from "@/components/bookings/GuestInquirySummary";
import { GuestStayHistory } from "@/components/guests/GuestStayHistory";
import { InquiryFitBadge } from "@/components/bookings/InquiryFitBadge";
import { PlaceBanNotice } from "@/components/bookings/PlaceBanNotice";
import { PitchPlanPanel } from "@/components/bookings/PitchPlanPanel";
import { StayCalendarNotes } from "@/components/bookings/StayCalendarNotes";
import { formatIsoDateShort, parseLocalIsoDate } from "@/lib/bookingDates";
import {
  formatBookingAddons,
  formatBookingSource,
  displayBookingNotes,
  vehicleFieldValue,
} from "@/lib/inquiryDisplay";
import { formatStayAmount } from "@/lib/money";
import { useArrivalTypes } from "@/lib/useArrivalTypes";
import { usePitchFeatures } from "@/lib/usePitchFeatures";
import { vehicleTypeLabel } from "@/lib/vehicle";
import { VEHICLE_FIELD_ROWS } from "@/lib/vehicleFields";
import type { Booking, Guest, Pitch } from "@/types/camping";

const WEEKDAY_SHORT = new Intl.DateTimeFormat("de-DE", { weekday: "short" });

function formatStayDate(isoDate: string): string {
  const weekday = WEEKDAY_SHORT.format(parseLocalIsoDate(isoDate)).replace(/\.$/, "");
  return `${weekday}, ${formatIsoDateShort(isoDate)}`;
}

function nightsLabel(nights: number): string {
  return nights === 1 ? "1 Nacht" : `${nights} Nächte`;
}

function partyLabel(adults: number, children: number, dogs: number): string {
  const parts = [adults === 1 ? "1 Erwachsener" : `${adults} Erwachsene`];
  if (children === 1) parts.push("1 Kind");
  else if (children > 0) parts.push(`${children} Kinder`);
  if (dogs === 1) parts.push("1 Hund");
  else if (dogs > 0) parts.push(`${dogs} Hunde`);
  return parts.join(" · ");
}

function FactChip({ children, tone = "default" }: { children: ReactNode; tone?: "default" | "accent" }) {
  return (
    <span
      className={
        tone === "accent"
          ? "inline-flex items-center rounded-full bg-accent-soft px-2.5 py-1 text-xs font-medium text-accent"
          : "inline-flex items-center rounded-full bg-fill-tertiary px-2.5 py-1 text-xs font-medium text-label"
      }
    >
      {children}
    </span>
  );
}

function DetailSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-separator bg-fill-tertiary/30 p-4">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-label-tertiary">{title}</h3>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function InfoItem({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value?.trim()) return null;
  return (
    <div>
      <dt className="text-[11px] font-medium uppercase tracking-wide text-label-tertiary">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium text-label">{value}</dd>
    </div>
  );
}

export interface InquiryDetailModalProps {
  open: boolean;
  booking: Booking | null;
  guest: Guest | null;
  pitches?: Pitch[];
  pitch?: Pitch | null;
  categoryName?: string | null;
  canEdit?: boolean;
  confirmLoading?: boolean;
  rejectLoading?: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  onReject?: () => void;
  onBookingUpdated?: (booking: Booking) => void;
  onOpenPitch?: (pitch: Pitch) => void;
}

export function InquiryDetailModal({
  open,
  booking,
  guest,
  pitches = [],
  pitch: _pitch,
  categoryName,
  canEdit = true,
  confirmLoading = false,
  rejectLoading = false,
  onClose,
  onConfirm,
  onReject,
  onBookingUpdated,
  onOpenPitch,
}: InquiryDetailModalProps) {
  const { labelFor: arrivalTypeLabel } = useArrivalTypes();
  const { features: pitchFeatures } = usePitchFeatures(open);

  if (!booking) return null;

  const actionBusy = confirmLoading || rejectLoading;
  const showActions = canEdit && booking.status === "requested" && (onConfirm || onReject);

  const adults = booking.adults ?? 2;
  const children = booking.children ?? 0;
  const dogs = booking.dogs ?? 0;
  const people = partyLabel(adults, children, dogs);

  const receivedAt = booking.created_at
    ? formatIsoDateShort(booking.created_at.slice(0, 10))
    : null;

  const arrivalType = arrivalTypeLabel(booking.arrival_type);
  const vehicleType =
    booking.vehicle_type && booking.vehicle_type !== booking.arrival_type
      ? vehicleTypeLabel(booking.vehicle_type)
      : null;
  const vehicleRows = VEHICLE_FIELD_ROWS.map((field) => ({
    label: field.label,
    value: vehicleFieldValue(booking, field.key),
  })).filter((row) => row.value);
  const addonLines = formatBookingAddons(booking.online_booking_addons);
  const featureNames = new Map(pitchFeatures.map((f) => [f.id, f.name]));
  const requestedFeatureNames = (booking.requested_feature_ids ?? [])
    .map((id) => featureNames.get(id))
    .filter((name): name is string => Boolean(name));
  const bookingMessage = displayBookingNotes(booking.notes);
  const sourceLabel = formatBookingSource(booking.source);
  const showSourceChip = Boolean(sourceLabel && booking.source !== "online");
  const staySummary = [nightsLabel(booking.nights), categoryName?.trim()].filter(Boolean).join(" · ");

  const footer = (
    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
      {showActions && onConfirm ? (
        <Button
          type="button"
          loading={confirmLoading}
          disabled={actionBusy}
          onClick={onConfirm}
        >
          {booking.place_ban_flagged ? "Trotz Platzverbot bestätigen" : "Anfrage bestätigen"}
        </Button>
      ) : null}
      {showActions && onReject ? (
        <Button
          type="button"
          variant="danger"
          loading={rejectLoading}
          disabled={actionBusy}
          onClick={onReject}
        >
          Anfrage ablehnen
        </Button>
      ) : null}
      <Button type="button" variant="ghost" className="sm:ml-auto" onClick={onClose}>
        Schließen
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Buchungsanfrage prüfen"
      description={`Anfrage ${booking.booking_number}`}
      size="xl"
      footer={footer}
    >
      <div className="space-y-4">
        {booking.place_ban_flagged ? <PlaceBanNotice booking={booking} /> : null}

        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={booking.status} />
          {booking.source === "online" ? (
            <span className="rounded-full bg-accent-soft px-2.5 py-0.5 text-xs font-medium text-accent">
              Online
            </span>
          ) : null}
          {showSourceChip ? (
            <span className="rounded-full bg-fill-tertiary px-2.5 py-0.5 text-xs font-medium text-label-secondary">
              {sourceLabel}
            </span>
          ) : null}
          {receivedAt ? (
            <span className="text-xs text-label-secondary">Eingang {receivedAt}</span>
          ) : null}
          <InquiryFitBadge offer={booking.inquiry_offer} />
        </div>

        {booking.inquiry_offer?.status === "impossible" ? (
          <p className="rounded-xl border border-status-red/30 bg-status-red/10 px-4 py-3 text-sm text-status-red">
            Für diesen Zeitraum ist kein Platz frei – auch nicht mit Umplanung anderer Aufenthalte
            oder einer anderen Platzkategorie.
          </p>
        ) : null}
        {(booking.inquiry_offer?.changes ?? []).length > 0 ? (
          <div className="rounded-xl border border-status-orange/30 bg-status-orange/10 px-4 py-3 text-sm text-label">
            <p className="font-medium">Abweichung von der ursprünglichen Anfrage</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {booking.inquiry_offer?.changes?.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            <p className="mt-2 text-label-secondary">
              Diese Punkte stehen in der E-Mail an den Gast.
            </p>
          </div>
        ) : null}

        <section className="overflow-hidden rounded-xl border border-separator bg-fill-tertiary/30">
          <div className="h-0.5 bg-accent" aria-hidden />
          <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-end sm:justify-between sm:p-5">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-label-tertiary">
                Zeitraum
              </p>
              <p className="mt-1 flex flex-wrap items-baseline gap-x-2 text-lg font-semibold tracking-tight text-label sm:text-xl">
                <span>{formatStayDate(booking.arrival_date)}</span>
                <span className="font-normal text-label-tertiary">→</span>
                <span>{formatStayDate(booking.departure_date)}</span>
              </p>
              {staySummary ? (
                <p className="mt-1 text-sm text-label-secondary">{staySummary}</p>
              ) : null}
              <StayCalendarNotes
                arrivalDate={booking.arrival_date}
                departureDate={booking.departure_date}
              />
            </div>
            <div className="sm:text-right">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-label-tertiary">
                Gesamtbetrag
              </p>
              <p className="mt-1 text-lg font-semibold tabular-nums tracking-tight text-label sm:text-xl">
                {formatStayAmount(booking.total_amount, booking.currency, booking.price_pending)}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 border-t border-separator bg-fill-tertiary/30 px-4 py-3 sm:px-5">
            <FactChip>{people}</FactChip>
            {arrivalType !== "—" ? <FactChip>{arrivalType}</FactChip> : null}
            {booking.wants_electricity === true ? <FactChip tone="accent">Mit Strom</FactChip> : null}
            {booking.wants_electricity === false ? <FactChip>Ohne Strom</FactChip> : null}
          </div>
          {requestedFeatureNames.length > 0 ? (
            <p className="border-t border-separator px-4 py-3 text-sm text-label-secondary sm:px-5">
              <span className="font-medium text-label">Wünsche (soweit möglich): </span>
              {requestedFeatureNames.join(", ")}
            </p>
          ) : null}
        </section>

        <GuestInquirySummary guest={guest} booking={booking} />

        {vehicleType || vehicleRows.length > 0 ? (
          <DetailSection title="Fahrzeug">
            <dl className="grid gap-3 sm:grid-cols-2">
              <InfoItem label="Fahrzeugtyp" value={vehicleType} />
              {vehicleRows.map((row) => (
                <InfoItem key={row.label} label={row.label} value={row.value} />
              ))}
            </dl>
          </DetailSection>
        ) : null}

        {addonLines.length > 0 ? (
          <DetailSection title="Zusatzleistungen">
            <ul className="space-y-1.5">
              {addonLines.map((line) => (
                <li key={line} className="text-sm font-medium text-label">
                  {line}
                </li>
              ))}
            </ul>
          </DetailSection>
        ) : null}

        {booking.companions?.length ? (
          <DetailSection title="Mitreisende">
            <BookingCompanionsPanel
              companions={booking.companions}
              className="space-y-3"
              hideTitle
            />
          </DetailSection>
        ) : null}

        {bookingMessage ? (
          <DetailSection title="Nachricht">
            <p className="border-l-2 border-accent/50 pl-3 text-sm leading-relaxed text-label whitespace-pre-wrap">
              {bookingMessage}
            </p>
          </DetailSection>
        ) : null}

        <PitchPlanPanel
          booking={booking}
          pitches={pitches}
          canEdit={canEdit}
          onUpdated={(updated) => onBookingUpdated?.(updated)}
          onOpenPitch={onOpenPitch}
        />

        {guest ? <GuestStayHistory guestId={guest.id} active={open} /> : null}

        {showActions ? (
          <p className="text-xs leading-relaxed text-label-tertiary">
            Bestätigen oder Ablehnen öffnet die E-Mail an den Gast. Empfänger, Betreff und Text
            können Sie dort noch anpassen.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
